// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/buildAskV2ReviewProgressScript.test.ts
import { type AskV2RailProgressItem } from "./types";

export const ASK_V2_REVIEW_PROGRESS_SCRIPT_STEP_IDS = [
  "script-plan",
  "script-load",
  "script-agents",
  "script-inspect",
  "script-comments",
] as const;

export type AskV2ReviewProgressScriptStepId =
  (typeof ASK_V2_REVIEW_PROGRESS_SCRIPT_STEP_IDS)[number];

/** Steps 0–3 finish within this budget; step 4 holds until the review completes. */
export const ASK_V2_REVIEW_PROGRESS_STAGGER_BUDGET_MS = 3 * 60 * 1000;

/** First step ("Making a plan") completes quickly so preflight feels responsive. */
export const ASK_V2_REVIEW_PROGRESS_PLAN_MIN_MS = 15_000;
export const ASK_V2_REVIEW_PROGRESS_PLAN_MAX_MS = 20_000;

/** Load assets finishes quickly; inspect claims holds the longest beat. */
const LOAD_ASSETS_MIN_MS = 15_000;
const LOAD_ASSETS_MAX_MS = 30_000;
const SPIN_UP_AGENTS_MIN_MS = 25_000;
const SPIN_UP_AGENTS_MAX_MS = 55_000;
const CHECK_CLAIMS_MIN_MS = 60_000;

export type AskV2ReviewProgressThresholdsMs = readonly [
  number,
  number,
  number,
  number,
];

export type AskV2ReviewProgressScriptPhase = "idle" | "running" | "completed";

function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function askV2ReviewProgressThresholdSeed(
  chatId: string,
  startedAt: number,
): number {
  let hash = 0;
  const input = `${chatId}:${startedAt}`;
  for (let index = 0; index < input.length; index += 1) {
    hash = (Math.imul(31, hash) + input.charCodeAt(index)) | 0;
  }
  return hash >>> 0;
}

/** Deterministic splits: plan 15–20s, short load, long inspect, 3-minute total. */
export function generateAskV2ReviewProgressThresholdsMs(
  seed: number,
): AskV2ReviewProgressThresholdsMs {
  const rand = mulberry32(seed);
  const planSpan =
    ASK_V2_REVIEW_PROGRESS_PLAN_MAX_MS - ASK_V2_REVIEW_PROGRESS_PLAN_MIN_MS;
  const planDuration =
    ASK_V2_REVIEW_PROGRESS_PLAN_MIN_MS + Math.floor(rand() * (planSpan + 1));

  let remaining = ASK_V2_REVIEW_PROGRESS_STAGGER_BUDGET_MS - planDuration;

  const loadSpan = LOAD_ASSETS_MAX_MS - LOAD_ASSETS_MIN_MS;
  const loadDuration = LOAD_ASSETS_MIN_MS + Math.floor(rand() * (loadSpan + 1));
  remaining -= loadDuration;

  const agentsMax = Math.min(
    SPIN_UP_AGENTS_MAX_MS,
    remaining - CHECK_CLAIMS_MIN_MS,
  );
  const agentsSpan = Math.max(0, agentsMax - SPIN_UP_AGENTS_MIN_MS);
  const agentsDuration =
    SPIN_UP_AGENTS_MIN_MS + Math.floor(rand() * (agentsSpan + 1));
  remaining -= agentsDuration;

  const inspectDuration = remaining;

  const durations = [
    planDuration,
    loadDuration,
    agentsDuration,
    inspectDuration,
  ];

  let cumulative = 0;
  const thresholds: number[] = [];
  for (const duration of durations) {
    cumulative += duration;
    thresholds.push(cumulative);
  }
  return [
    thresholds[0]!,
    thresholds[1]!,
    thresholds[2]!,
    thresholds[3]!,
  ] as AskV2ReviewProgressThresholdsMs;
}

function stepStatus(
  index: number,
  elapsedMs: number,
  running: boolean,
  thresholdsMs: AskV2ReviewProgressThresholdsMs,
): AskV2RailProgressItem["status"] {
  if (!running) return "done";

  if (index < 4) {
    const completeAt = thresholdsMs[index];
    const startAt = index === 0 ? 0 : thresholdsMs[index - 1];
    if (elapsedMs >= completeAt) return "done";
    if (elapsedMs >= startAt) return "active";
    return "pending";
  }

  if (elapsedMs >= thresholdsMs[3]) {
    return "active";
  }
  return "pending";
}

export function buildAskV2ReviewProgressScript(input: {
  elapsedMs: number;
  phase: AskV2ReviewProgressScriptPhase;
  labels: Record<AskV2ReviewProgressScriptStepId, string>;
  thresholdsMs: AskV2ReviewProgressThresholdsMs;
}): AskV2RailProgressItem[] {
  if (input.phase === "idle") return [];

  const running = input.phase === "running";

  return ASK_V2_REVIEW_PROGRESS_SCRIPT_STEP_IDS.map((id, index) => ({
    id,
    label: input.labels[id],
    status: stepStatus(index, input.elapsedMs, running, input.thresholdsMs),
  }));
}
